import React, { useState, useEffect, useRef } from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { messageAPI, channelAPI } from '../../lib/api';
import { cn, getInitials } from '../../lib/utils';
import {
    Search,
    X,
    Hash,
    MessageSquare,
    Calendar,
    User,
    Loader2,
    ArrowRight
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';

const SearchMessages = ({ isOpen, onClose, onSelectMessage }) => {
    const { currentWorkspace } = useWorkspace();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [channels, setChannels] = useState([]);
    const [selectedChannel, setSelectedChannel] = useState(null);
    const searchInputRef = useRef(null);
    const debounceRef = useRef(null);

    // Load channels
    useEffect(() => {
        if (currentWorkspace && isOpen) {
            loadChannels();
        }
    }, [currentWorkspace, isOpen]);

    // Focus input when modal opens
    useEffect(() => {
        if (isOpen) {
            setTimeout(() => searchInputRef.current?.focus(), 100);
        }
    }, [isOpen]);

    const loadChannels = async () => {
        try {
            const res = await channelAPI.getByWorkspace(currentWorkspace.id);
            setChannels(res.data.channels || []);
            if (res.data.channels?.length > 0) {
                setSelectedChannel(res.data.channels[0]);
            }
        } catch (error) {
            console.error('Failed to load channels:', error);
        }
    };

    const handleSearch = async (searchQuery) => {
        if (!searchQuery.trim() || !selectedChannel) {
            setResults([]);
            return;
        }

        setLoading(true);
        try {
            const res = await messageAPI.search(selectedChannel.id, searchQuery);
            setResults(res.data || []);
        } catch (error) {
            console.error('Search failed:', error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    const handleQueryChange = (e) => {
        const newQuery = e.target.value;
        setQuery(newQuery);

        // Debounce search
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            handleSearch(newQuery);
        }, 300);
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        if (date.toDateString() === today.toDateString()) {
            return `Hari ini, ${date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}`;
        } else if (date.toDateString() === yesterday.toDateString()) {
            return `Kemarin, ${date.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}`;
        }
        return date.toLocaleDateString('id-ID', { 
            day: 'numeric', 
            month: 'short', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const highlightMatch = (text, query) => {
        if (!query.trim()) return text;
        
        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);
        
        return parts.map((part, index) => 
            regex.test(part) ? (
                <mark key={index} className="bg-yellow-200 px-0.5 rounded">
                    {part}
                </mark>
            ) : part
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Search className="w-5 h-5" />
                        Cari Pesan
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 flex-1 overflow-hidden flex flex-col">
                    {/* Channel selector */}
                    <div className="flex gap-2 flex-wrap">
                        {channels.map(ch => (
                            <button
                                key={ch.id}
                                onClick={() => {
                                    setSelectedChannel(ch);
                                    setResults([]);
                                    if (query.trim()) {
                                        handleSearch(query);
                                    }
                                }}
                                className={cn(
                                    'px-3 py-1.5 rounded-full text-sm flex items-center gap-1 transition-colors',
                                    selectedChannel?.id === ch.id
                                        ? 'bg-brand-primary text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                )}
                            >
                                <Hash className="w-3 h-3" />
                                {ch.name}
                            </button>
                        ))}
                    </div>

                    {/* Search input */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                        <Input
                            ref={searchInputRef}
                            value={query}
                            onChange={handleQueryChange}
                            placeholder="Ketik untuk mencari pesan..."
                            className="pl-10 pr-10"
                        />
                        {query && (
                            <button
                                onClick={() => {
                                    setQuery('');
                                    setResults([]);
                                }}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Results */}
                    <div className="flex-1 overflow-y-auto space-y-2">
                        {loading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                            </div>
                        ) : results.length > 0 ? (
                            results.map(message => (
                                <button
                                    key={message.id}
                                    onClick={() => {
                                        onSelectMessage && onSelectMessage(message, selectedChannel);
                                        onClose();
                                    }}
                                    className="w-full p-3 bg-gray-50 hover:bg-gray-100 rounded-lg text-left transition-colors group"
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                                            {getInitials(message.user?.name || 'U')}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-medium text-sm">
                                                    {message.user?.name || 'Unknown'}
                                                </span>
                                                <span className="text-xs text-gray-400 flex items-center gap-1">
                                                    <Calendar className="w-3 h-3" />
                                                    {formatDate(message.created_at)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-gray-700 line-clamp-2">
                                                {highlightMatch(message.content, query)}
                                            </p>
                                            <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                                                <Hash className="w-3 h-3" />
                                                {selectedChannel?.name}
                                            </div>
                                        </div>
                                        <ArrowRight className="w-4 h-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                </button>
                            ))
                        ) : query.trim() ? (
                            <div className="text-center py-8 text-gray-400">
                                <MessageSquare className="w-8 h-8 mx-auto mb-2" />
                                <p className="text-sm">Tidak ada hasil untuk "{query}"</p>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-gray-400">
                                <Search className="w-8 h-8 mx-auto mb-2" />
                                <p className="text-sm">Ketik kata kunci untuk mencari pesan</p>
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default SearchMessages;
